import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Badge, Card, CardBody, CardTitle } from '../../ui';
import { listRoster } from '../../shared/queries';
import type { ClientRow } from '../../shared/queries';
import { formatMoment } from '../../shared/formatDate';
import { supabase } from '../../shared/supabase/client';
import { InviteActions } from './InviteActions';
import { InviteClientForm } from './InviteClientForm';

/**
 * The coach's roster.
 *
 * `listRoster` carries no `coach_id` filter and never will: the policy
 * `client_select_coach` supplies it. That is what the isolation tests assert,
 * and it is why a coach cannot see another instance's clients even if this file
 * were rewritten carelessly.
 */
export function RosterPage() {
  const roster = useQuery({
    queryKey: ['roster'],
    queryFn: () => listRoster(supabase),
  });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-ink text-2xl font-semibold">Clients</h1>
        <p className="text-ink-soft mt-1 text-sm">
          Everyone you coach. An invited client appears here before they have an account.
        </p>
      </div>

      <InviteClientForm />

      {roster.isPending ? <p className="text-ink-muted text-sm">Loading…</p> : null}

      {roster.isError ? (
        <p role="alert" className="text-danger text-sm">
          The roster could not be loaded.
        </p>
      ) : null}

      {roster.data?.length === 0 ? (
        <Card>
          <CardTitle>No clients yet</CardTitle>
          <CardBody>Invite someone by email and they appear here as soon as you do.</CardBody>
        </Card>
      ) : null}

      {roster.data && roster.data.length > 0 ? (
        <ul className="flex flex-col gap-3">
          {roster.data.map((client) => (
            <li key={client.id}>
              <RosterRow client={client} />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function RosterRow({ client }: { client: ClientRow }) {
  return (
    <Card className="flex flex-wrap items-center justify-between gap-3">
      <div>
        <Link
          to={`/admin/clients/${client.id}`}
          className="text-ink font-medium underline-offset-2 hover:underline"
        >
          {client.display_name}
        </Link>
        <p className="text-ink-muted text-xs">{client.email}</p>
      </div>

      <div className="flex items-center gap-3">
        {client.first_signed_in_at === null ? null : (
          <span className="text-ink-muted text-xs">
            First signed in {formatMoment(client.first_signed_in_at)}
          </span>
        )}
        <StatusBadge status={client.status} />
        {client.status === 'invited' ? (
          <InviteActions clientId={client.id} email={client.email} />
        ) : null}
      </div>
    </Card>
  );
}

export function StatusBadge({ status }: { status: string }) {
  if (status === 'active') return <Badge tone="training">Active</Badge>;
  if (status === 'invited') return <Badge tone="attention">Invited</Badge>;
  return <Badge>{status}</Badge>;
}
